import React, { useState, useEffect } from 'react';
import EditProjectGroupPopup from '../EditProjectGroupPopup';
import DeleteProjectGroupPopup from '../DeleteProjectGroupPopup';
import Animate from '../../../components/Animate';
import Home from '../../Home';
import ProjectHeader from '../../TasksContainer/ProjectHeader';
import ProjectStatusGraphic from '../../../components/graphics/ProjectStatusGraphic';
import PDFGenerator from '../../../components/PDFGenerator';
import ReduxConstants from '../../../constants/ReduxConstants';
import closeoutsIcon from '../../../assets/images/generate-closeout.svg';
import deleteIcon from '../../../assets/images/trash.svg';
import kmzIcon from '../../../assets/images/file-type-doc-icon.svg';
import kmzIconDisabled from '../../../assets/images/file-type-doc-icon-disabled.svg';
import editGroupIcon from '../../../assets/images/edit-project-group.png';
import kmzService from '../../../services/kmzService';
import projectGroupService from '../../../services/projectGroupService';

import './ProjectGroup.scss';
import DataModel from '../../../models/DataModel';

const ProjectGroup = ({ projectGroup, projectsList, currentCompany, permissions, handleCloseoutGenerated, handleProjectClick, closeout_templates, handleEditGroup, onDelete }) => {

  const accent = currentCompany.accent_color || '#9b9b9b';
  const [isExpand, setIsExpand] = useState(false);
  const [contracting, setContracting] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [isEditPopupActive, setIsEditPopupActive] = useState(false);
  const [isDeletePopupActive, setIsDeletePopupActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Function to calculate the group completion percentage
  const calculateGroupCompletionPercentage = (projects) => {
    if (projects.length === 0) return 0;
    const totalPercentage = projects.reduce((acc, project) => {
      const completionPercentage = project.tasks_count ? (project.tasks_completed_count / project.tasks_count) * 100 : 0;
      return acc + completionPercentage;
    }, 0);
    return Math.round(totalPercentage / projects.length);
  };

  const isAnyProjectInProgress = (projects) => {
    return projects.some(project => {
      return project.status === 'in_progress';
    });
  };
  
  const anyInProgress = isAnyProjectInProgress(projectGroup.projects); // This will return true if any project is in progress, otherwise false.
  
  const groupCompletionPercentage = calculateGroupCompletionPercentage(projectGroup.projects);

  const handleExpandDone = () => {
    setExpanding(false);
    setIsExpand(true);
  };

  const handleExpandAndCollapse = () => {
    setIsExpand(!isExpand);
    if (!isExpand) {
      setExpanding(true);
    } else {
      setContracting(true);
    }
  };

  const handleContractDone = () => {
    setContracting(false);
    setIsExpand(false);
  };

  const showEditPopup = () => {
    if(isExpand){
      handleExpandAndCollapse();
    }
    setIsEditPopupActive(true);
  };

  const hideEditPopup = () => {
    setIsEditPopupActive(false);
  };

  const showDeletePopup = () => {
    setIsDeletePopupActive(true);
  };

  const hideDeletePopup = () => {
    setIsDeletePopupActive(false);
  };

  const handleDeleteGroup = () => {
    projectGroupService.deleteProjectGroup(projectGroup.id, currentCompany.id)
    .then(response=>{
      if (!response.error){
        if(onDelete){
          onDelete();
          DataModel.displayInfoErrorMessage('The group was deleted successfully.', 4000, 1);
        }
      }
        
    });
  };

  const editGroup = () =>{
    setIsLoading(true);
    if(handleEditGroup){
      handleEditGroup().then(()=>{
        setIsLoading(false);
        if(!isExpand){
          handleExpandAndCollapse();
        }
      });
    }
  };

  const createProjectRows = (groupedProjects) => {
    const filteredProjects = groupedProjects
      .filter(project => project.is_favorite)
      .map(project => project.id);

    const sortedProjects = groupedProjects.sort((a, b) => {
      if (filteredProjects.includes(a.id)) return -1;
      return 1;
    });

    const finalSort = [
      ...sortedProjects.slice(0, filteredProjects.length).reverse(),
      ...sortedProjects.slice(filteredProjects.length),
    ];

    return finalSort.map((project, index) => {
      const canSelect = !project.archived_at;
      return (
        <div
          className={'project-summary-row' + (canSelect ? ' selectable' : '')}
          key={index}
          id={project.id}
          onClick={(e) => handleProjectClick(e, project.id, canSelect)}
        >
          <ProjectHeader
            currentCompany={currentCompany}
            project={project}
            type='summary-page'
            permissions={permissions}
            inProjectsTab={true}
            useSummaryDataOnly={true}
          />
          <div className='graphic-container'>
            <div className='divider' style={{ borderColor: accent }}></div>
            <ProjectStatusGraphic project={project} accent={accent} />
            <div className='divider' style={{ borderColor: accent }}></div>
            <div className='generate-button'>
              <PDFGenerator
                class={'generate-button__text'}
                project={project}
                handleCloseoutGenerated={handleCloseoutGenerated}
                closeout_templates={closeout_templates || 'wait'}
                text={'Generate Closeout'}
                closeout_client_data={{ destination: ReduxConstants.CLOSEOUT_SUMMARY }}
                fromProjectSummary={true}
              >
                <img
                  className='generate-button__icon'
                  src={closeoutsIcon}
                  alt={'Generate closeout of project'}
                />
              </PDFGenerator>
            </div>
            <div className='divider' style={{ borderColor: accent }}></div>
          </div>
        </div>
      );
    });
  };

  const downloadKmz = () =>{
    kmzService.generateKmzFile('project_group', projectGroup.id)
    .then(response=>{
      if(response.error){
        const errorMesssage = response.error.response.data.error;
        DataModel.displayWarnErrorMessage(errorMesssage, 4000, 2);
      }
      else{
        DataModel.displayInfoErrorMessage('Your Project Group KMZ file generation request has been received. You will be notified when the file is ready to download.', 4000, 1);
      }
    });
  };

  let animate = null;
  let savedHeight;
  if (expanding) {
    const element = document.getElementById('projects-container-' + projectGroup.id);
    savedHeight = element && element.clientHeight || element.scrollHeight;
    animate = (
      <Animate
        component_id={'projects-container-' + projectGroup.id}
        key={'shared-project-group-container-expand-' + projectGroup.id}
        property={{ height: { startVal: 0, endVal: savedHeight } }}
        duration={250}
        completionCallback={handleExpandDone}
      />
    );
  } else if (contracting) {
    animate = (
      <Animate
        component_id={'projects-container-' + projectGroup.id}
        key={'shared-project-group-container-contract-' + projectGroup.id}
        property={{ height: { startVal: savedHeight, endVal: 0 } }}
        duration={250}
        completionCallback={handleContractDone}
      />
    );
  }

  return (
    isLoading ?
      <Home type={'referenceDocuments'} message={' '} /> :
      (<div className='project-group-container'>
        {isEditPopupActive && (
          <EditProjectGroupPopup handleClosePopup={hideEditPopup} projectGroup={projectGroup} projectsList={projectsList} handleEditGroup={editGroup}/>
        )}
        {isDeletePopupActive && (
          <DeleteProjectGroupPopup handleClosePopup={hideDeletePopup} projectGroup={projectGroup} haveProjects={projectsList.length > 0} projectsList={projectsList} handleDeleteGroup={handleDeleteGroup}/>
        )}
        <div className='project-group-header'>
          <div className={'left-content'} onClick={handleExpandAndCollapse}>
            <div className="show-hide-button">
              {isExpand ? <div className='contractor' /> : <div className='expandor' />}
            </div>
            <div className='group-name'>{projectGroup.name.toUpperCase()}</div>
            <div className='group-project-count'>
              {`(${projectsList.length} project${projectsList.length > 1 ? 's' : ''})`}
            </div>
          </div>
          <div className="group-status">
            <ProjectStatusGraphic
              accent={accent}
              groupCompletionPercentage={groupCompletionPercentage} // New prop
              anyInProgress = {anyInProgress}
            />
          </div>
          <div className='divider' style={{ borderColor: accent }}></div>
          <div onClick={showEditPopup} className='group-buttons'>
            <img className="group-button-image" src={editGroupIcon} alt="Select"/>
          </div>
          <div className='divider' style={{ borderColor: accent }}></div>
          <div onClick={showDeletePopup} className="group-buttons">
            <img className="group-button-image" src={deleteIcon} alt="Delete Group"/>
          </div>
        </div>
        {animate}
        <div className={'projects-container'} id={'projects-container-' + projectGroup.id}>
          {createProjectRows(projectGroup.projects)}
        </div>
      </div>)
      
  );
};

export default ProjectGroup;



// WEBPACK FOOTER //
// ./src/containers/ProjectSummaryContainer/ProjectGroup/index.js